import { observable, flow, action } from 'mobx';
import { apiState, apiResponse, ApiError } from '../../Api';
import { RootStore } from '../createStore';
import { ProfessorComMonoEPreModel, ProfessorModel } from './Model';
import {
  fetchProfessores,
  fetchProfessoresComPaginacao,
  fecthProfessorComSuasMonografiasEPreProjetos,
  agendarBanca,
  editarDataAgendamento,
  fetchConvitesPendentes,
  fetchConvitesAceitos,
  responderConvite,
  recuperarInformacoesConvite,
  ativarDesativarUmProfessor,
  inserirMonografia,
  fetchAlunosAtivos,
  ativarDesativarMembroBanca
} from './Service';
import { ValidationError } from '../IValidator';
import { MembroDaBancaModel } from '../Banca';
import { MonografiaModel } from '../Monografia';
import {alterarVagasPreProjeto} from './Service';
import { AlunoModel } from '../Aluno';


export class ProfessorStore {
  /*
    Properties
  */
  @observable public professores: Array<ProfessorModel> = [];
  @observable public professor: ProfessorComMonoEPreModel;
  @observable public convitesPendentes: Array<MembroDaBancaModel> = [];
  @observable public alunosAtivos: Array<AlunoModel> = [];
  @observable public convitesAceitos: Array<MembroDaBancaModel> = [];
  @observable public vagasPreProjeto: number = 0;
  @observable public membros: Array<ProfessorModel> = observable([
    new ProfessorModel()
  ]);
  @observable public suplentes: Array<ProfessorModel> = observable([
    new ProfessorModel()
  ]);

  @observable public dia: string = '';
  @observable public hora: string = '';

  
  @observable alunosAtivosState: apiState = apiState.loading;
  @observable professoresState: apiState = apiState.loading;
  @observable convitesPendentesState: apiState = apiState.loading;
  @observable convitesAceitosState: apiState = apiState.loading;
  @observable public agendamentoRealizado: boolean = false;
  @observable public conviteRespondido: boolean = false;
  @observable public convite: MonografiaModel = new MonografiaModel();

  /*
    Validators
  */
  @observable public professorValidationError: ValidationError<{
    data: string;
    banca: ProfessorModel[];
    suplentes: ProfessorModel[];
  }>;
  @observable public conviteValidationError: ValidationError<{ data: string }>;
  @observable public editarDataAgendamentoValidationError: ValidationError<{ dataHora: string }>;

  @observable professorListQuery = {
    search: '',
    page: 1,
    pageSize: 10
  }

  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  @action
  setValidationError = (
    validationError: ValidationError<{ data: string; banca: ProfessorModel[], suplentes: ProfessorModel[] }>
  ) => {
    this.professorValidationError = validationError;
  };

  @action
  updateAttribute = (name: string, value: any) => {
    this[name] = value;
  };

  @action
  updateObject = (object: string, field: string, value: any) => {
    this[object][field] = value;
  };

  @action
  updateArray = (arrayName: string, index: number, value: any) => {
    this[arrayName][index] = value;
  };

  @action
  updateArrayAttribute = (
    arrayName: string,
    index: number,
    field: string,
    value: string | number
  ) => {
    this[arrayName][index][field] = value;
  };

  @action
  addSuplente = () => {
    this.suplentes.push(new ProfessorModel());
  }

  @action
  removerSuplente = (idx: number) => {
    this.suplentes.splice(idx, 1);
  }

  @action
  resetAgendamento = () => (this.agendamentoRealizado = false);

  getProfessores = flow(function* (this: ProfessorStore) {
    this.professoresState = apiState.loading;

    const professores: apiResponse<ProfessorModel[]> = yield fetchProfessores();
    this.professoresState = professores.status;

    if (professores.status === apiState.done)
      this.professores = professores.data;
  });

  getProfessoresComPaginacao = flow(function* (this: ProfessorStore, concat: boolean = false) {
    this.professoresState = apiState.loading;

    const { page, pageSize, search } = this.professorListQuery;

    const fetchProfessores: apiResponse<ProfessorModel[]> = yield fetchProfessoresComPaginacao(
      page, pageSize, search
    );

    this.professoresState = fetchProfessores.status;

    this.professores = concat
      ? this.professores.concat(fetchProfessores.data)
      : fetchProfessores.data;
  });

  getProfessorComSuasMonografiasEPreProjetos = flow(function* (this: ProfessorStore, professorId: number) {
    this.professoresState = apiState.loading

    const prof = yield fecthProfessorComSuasMonografiasEPreProjetos(professorId)

    this.professoresState = prof.status
    this.professor = prof.data;
  });

  agendarBanca = flow(function* (this: ProfessorStore, monografiaId: number) {
    this.professoresState = apiState.loading;

    const agendamento: apiResponse<ApiError[] | undefined> = yield agendarBanca(
      monografiaId,
      this.membros,
      this.suplentes,
      this.dia,
      this.hora
    );
    this.professoresState = agendamento.status;

    this.agendamentoRealizado = this.professoresState === apiState.done;

    if (
      agendamento.status === apiState.error ||
      agendamento.status === apiState.badRequest
    )
      this.professorValidationError!.global = agendamento.data!.flatMap(
        (err: ApiError) => err.errorMessages
      );
  });

  getConvitesPendentes = flow(function* (this: ProfessorStore) {
    this.convitesPendentesState = apiState.loading;

    const convites: apiResponse<MembroDaBancaModel[]> = yield fetchConvitesPendentes();
    this.convitesPendentesState = convites.status;

    if (convites.status === apiState.done) this.convitesPendentes = convites.data;
  });

  getAlunosAtivos = flow(function* (this: ProfessorStore) {
    this.alunosAtivosState = apiState.loading;

    const alunos: apiResponse<AlunoModel[]> = yield fetchAlunosAtivos();
    this.alunosAtivosState = alunos.status;

    if (alunos.status === apiState.done) this.alunosAtivos = alunos.data;
  });
 
  getConvitesAceitos = flow(function* (this: ProfessorStore) {
    this.convitesAceitosState = apiState.loading;

    const convites: apiResponse<MembroDaBancaModel[]> = yield fetchConvitesAceitos();
    this.convitesAceitosState = convites.status;

    if (convites.status === apiState.done) this.convitesAceitos = convites.data;
  });

  responderConvite = flow(function* (
    this: ProfessorStore,
    monografiaId: number,
    resposta: boolean,
    justificativa?: string
  ) {
    this.professoresState = apiState.loading;

    const response: apiResponse<
      ApiError[] | undefined
    > = yield responderConvite(monografiaId, resposta, justificativa);
    this.professoresState = response.status;

    this.conviteRespondido = this.professoresState === apiState.done;
  });

  recuperarInformacoesConvite = flow(function* (
    this: ProfessorStore,
    monografiaId: number
  ) {
    this.professoresState = apiState.loading;

    const response: apiResponse<MonografiaModel> = yield recuperarInformacoesConvite(
      monografiaId
    );
    this.professoresState = response.status;

    if (response.status === apiState.done) this.convite = response.data;
  });

  editarDataAgendamento = flow(function* (this: ProfessorStore, monografiaId: number) {
    this.professoresState = apiState.loading;

    const novoAgendamento: apiResponse<ApiError[] | undefined> = yield editarDataAgendamento(
      monografiaId,
      this.dia,
      this.hora
    );
    this.professoresState = novoAgendamento.status;

    this.agendamentoRealizado = this.professoresState === apiState.done;

    if (
      novoAgendamento.status === apiState.error ||
      novoAgendamento.status === apiState.badRequest
    )
      this.professorValidationError!.global = novoAgendamento.data!.flatMap(
        (err: ApiError) => err.errorMessages
      );
  });

  alterarVagasPreProjeto = flow(function* (this: ProfessorStore, id: number, vagasPreProjeto:number) {
    this.professoresState = apiState.loading;

    const result: apiResponse<undefined> = yield alterarVagasPreProjeto(
      id,
      vagasPreProjeto,
    );
    this.professoresState = result.status;
  });

  inserirMonografia = flow(function* (this: ProfessorStore, alunoId: number,
    professorId:number, titulo: string) {
   this.professoresState = apiState.loading;

   const result: apiResponse<undefined> = yield inserirMonografia(
     alunoId,
     professorId,
     titulo
   );
   this.professoresState = result.status;
 });


  ativarDesativarUmProfessor = flow(function* (this: ProfessorStore) {
    const professor = this.professor.professor
    if (professor.ativo) {
        const response: apiResponse<ApiError[]> = yield ativarDesativarUmProfessor(professor.id, false);

        if (response.status === apiState.done) {
            this.professor.professor.ativo = false;
        }
    } else {
        const response: apiResponse<ApiError[]> = yield ativarDesativarUmProfessor(professor.id, true);

        if (response.status === apiState.done) {
            this.professor.professor.ativo = true;
        }
    }
});

ativarDesativarUmProfessorComoMembroBanca = flow(function* (this: ProfessorStore) {
    const professor = this.professor.professor
    if (professor.ativoBanca) {
        const response: apiResponse<ApiError[]> = yield ativarDesativarMembroBanca(professor.id, false);

        if (response.status === apiState.done) {
            this.professor.professor.ativoBanca = false;
        }
    } else {
        const response: apiResponse<ApiError[]> = yield ativarDesativarMembroBanca(professor.id, true);

        if (response.status === apiState.done) {
            this.professor.professor.ativoBanca = true;
        }
    }
});
}
